import { PDFDocument } from 'pdf-lib';

/**
 * Merge multiple PDFs (in order) into one and trigger a download.
 *
 * @param {{ arrayBuffer: ArrayBuffer }[]} items - PDF items to merge, in order
 * @param {string} outputName - Desired output filename
 */
export async function mergePdfs(items, outputName = 'merged.pdf') {
  const mergedDoc = await PDFDocument.create();

  for (const item of items) {
    const srcDoc = await PDFDocument.load(item.arrayBuffer);
    const pageCount = srcDoc.getPageCount();
    const indices = Array.from({ length: pageCount }, (_, i) => i);
    const copiedPages = await mergedDoc.copyPages(srcDoc, indices);
    copiedPages.forEach((page) => mergedDoc.addPage(page));
  }

  const pdfBytes = await mergedDoc.save();
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);

  const safeOutputName = outputName.toLowerCase().endsWith('.pdf')
    ? outputName
    : `${outputName}.pdf`;

  const link = document.createElement('a');
  link.href = url;
  link.download = safeOutputName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  setTimeout(() => URL.revokeObjectURL(url), 15000);
}

/**
 * Read a File object as an ArrayBuffer.
 */
export function readFileAsArrayBuffer(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Build and download a reordered PDF using pdf-lib.
 *
 * @param {ArrayBuffer} originalArrayBuffer - Source PDF bytes
 * @param {number[]} pageIndices - 0-based page indices in the desired output order
 * @param {string} fileName - Output file name (with or without .pdf extension)
 */
export async function exportReorderedPdf(originalArrayBuffer, pageIndices, fileName) {
  const srcDoc = await PDFDocument.load(originalArrayBuffer);
  const newDoc = await PDFDocument.create();

  const copiedPages = await newDoc.copyPages(srcDoc, pageIndices);
  copiedPages.forEach((page) => newDoc.addPage(page));

  const pdfBytes = await newDoc.save();
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);

  const outputName = fileName.toLowerCase().endsWith('.pdf') ? fileName : `${fileName}.pdf`;

  const link = document.createElement('a');
  link.href = url;
  link.download = outputName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Revoke after a small delay to ensure the download has started
  setTimeout(() => URL.revokeObjectURL(url), 15000);
}
